/**
 * Pagina WhatsApi - Mensagens.
 *
 * TASK-2215: Tabela paginada com filtros (status, source, target, periodo)
 * sobre o endpoint /api/v1/tenants/{id}/whatsapi/messages.
 *
 * TASK-2216: Modal de detalhes ao clicar em linha, acao "Reenviar"
 * (status=failed + permissao whatsapi:resend) e delete com modal de
 * confirmacao separado (permissao whatsapi:delete). Mutations via
 * React Query + toast de feedback.
 *
 * TASK-2219: Form de envio (gate whatsapi:send), indicator de status WS
 * no header e invalidacao automatica via useWhatsApiWs ao receber
 * eventos message.sent / message.failed / message.deleted.
 *
 * Modo organizacao (spec 2026-10-23 §5.3/§5.4): sem tenant, a tela fala com
 * /api/v1/organizations/{id}/whatsapi/..., com o gate de envio no `pode.send` da
 * organizacao e as chaves de GET .../chaves. Sem reenviar nem apagar, e sem
 * WebSocket (ele autentica com chave em texto): a lista se atualiza a cada 15 s.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Stack, Group, Select, TextInput, Textarea, Table, Text, Pagination, Skeleton,
  Alert, Button, Modal, Divider, Box,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconAlertCircle, IconFilterOff, IconRefresh, IconSend, IconTrash,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { whatsapiApi } from '@/api/whatsapi'
import type {
  WaApiKey, WaMessage, WaMessageListResponse, WaMessageStatus, WaSendPayload,
} from '@/api/whatsapi'
import { whatsapiOrgApi } from '@/api/whatsapiOrg'
import { useCan } from '@/store/auth'
import { DataCard } from '@/components/ui'
import { exigirTenant } from './exigirTenant'
import { exigirOrganizacao } from './exigirOrganizacao'
import { WhatsApiStatusBadge } from '@/components/whatsapi/StatusBadge'
import { WhatsApiWsIndicator } from '@/components/whatsapi/WsIndicator'
import { SeletorEscopo } from '@/components/whatsapi/SeletorEscopo'
import { TituloDoEscopo } from '@/components/whatsapi/TituloDoEscopo'
import { useWhatsApiWs } from '@/hooks/useWhatsApiData'
import { useEscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

const PAGE_SIZE = 20
const FEEDBACK_AUTO_CLEAR_MS = 4000
const WA_TARGET_SUFFIX = '@s.whatsapp.net'
/** Sem WebSocket no modo organizacao (spec §5.4): a lista se atualiza sozinha. */
const POLLING_ORG_MS = 15_000

const STATUS_OPTIONS: { value: WaMessageStatus; label: string }[] = [
  { value: 'queued',     label: 'Na fila' },
  { value: 'sent',       label: 'Enviado' },
  { value: 'delivered',  label: 'Entregue' },
  { value: 'read',       label: 'Lido' },
  { value: 'failed',     label: 'Falhou' },
  { value: 'deleted',    label: 'Deletado' },
]

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function normalizeTarget(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (trimmed.endsWith(WA_TARGET_SUFFIX)) return trimmed
  return `${trimmed}${WA_TARGET_SUFFIX}`
}

interface ApiError {
  response?: { status?: number; data?: { detail?: string } }
}

function resolveSendError(err: unknown): string {
  const status = (err as ApiError)?.response?.status
  if (status === 429) return 'Limite de envio atingido. Tente novamente em alguns segundos.'
  if (status === 502) return 'Serviço WhatsApi indisponível no momento.'
  const detail = (err as ApiError)?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  return 'Falha ao enviar a mensagem.'
}

/** Codigos de recusa das rotas por organizacao (spec 2026-10-23 §6). */
const RECUSA_DO_ENVIO_DA_ORG: Record<string, string> = {
  forbidden: 'Você não tem permissão para enviar mensagens nesta organização.',
  chave_nao_encontrada:
    'A chave escolhida não está ativa nesta organização. Atualize a página e escolha outra.',
  gateway_fora_do_registro:
    'O gateway desta chave não está registrado. Confira a chave em Administração → Gateways WhatsApp.',
  gateway_de_outra_organizacao:
    'O gateway da chave pertence a outra organização. Confira a chave em Administração → Gateways WhatsApp.',
  whatsapi_recusou: 'O WhatsApi recusou o envio.',
  whatsapi_limite: 'Limite de envio atingido. Tente novamente em alguns segundos.',
  envio_falhou: 'O gateway não conseguiu enviar. A mensagem ficou na lista como falha.',
  whatsapi_indisponivel: 'Serviço WhatsApi indisponível no momento.',
  whatsapi_sem_credencial: 'O ConsoleAI está sem a credencial do WhatsApi. Avise um administrador.',
  auditoria_falhou_mensagem_enviada:
    'A mensagem foi enviada, mas o registro de auditoria falhou. Não reenvie e avise um administrador.',
  envio_invalido: 'O WhatsApi recusou os dados do envio. Confira o target e a mensagem.',
}

function resolveOrgSendError(err: unknown): string {
  const detail = (err as ApiError)?.response?.data?.detail
  return (typeof detail === 'string' && RECUSA_DO_ENVIO_DA_ORG[detail]) || 'Falha ao enviar a mensagem.'
}

function truncate(value: string, max = 60): string {
  if (value.length <= max) return value
  return value.slice(0, max - 1) + '…'
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pt-BR', {
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  })
}

function formatNullableDateTime(iso: string | null): string {
  if (!iso) return '—'
  return formatDateTime(iso)
}

interface DetailRowProps {
  label: string
  children: React.ReactNode
}

function DetailRow({ label, children }: DetailRowProps) {
  return (
    <Group gap="xs" align="flex-start" wrap="nowrap">
      <Text size="sm" c="dimmed" w={160} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Box
        style={{
          flex: 1,
          fontSize: 'var(--mantine-font-size-sm)',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </Box>
    </Group>
  )
}

export function WhatsApiMessages() {
  const { escopo, opcoes, escolher, nome, carregando } = useEscopoWhatsApi()
  // Enquanto o escopo não se sabe (organização ativa e lista a caminho), nada é pedido:
  // sem isso a tela abre no tenant e troca depois (spec 2026-11-02 §5.1).
  const tenantId = !carregando && escopo.tipo === 'tenant' ? escopo.tenantId : undefined
  const orgId = !carregando && escopo.tipo === 'organizacao' ? escopo.orgId : undefined
  const modoOrg = orgId != null
  // Separa o cache da lista; o tenant segue com o id puro (o WebSocket invalida por ele).
  const chaveEscopo = modoOrg ? `org:${orgId}` : tenantId
  const can = useCan()
  const queryClient = useQueryClient()

  const [page, setPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState<WaMessageStatus | ''>('')
  const [filterSource, setFilterSource] = useState('')
  const [filterTarget, setFilterTarget] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')

  const [selectedMessage, setSelectedMessage] = useState<WaMessage | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  // ─── Form de envio (TASK-2219) ──────────────────────────────────────────
  const canSend = escopo.tipo === 'organizacao' ? escopo.pode.send : can('whatsapi', 'send')
  const [formApiKeyId, setFormApiKeyId] = useState<string | null>(null)
  const [formTarget, setFormTarget] = useState('')
  const [formMessage, setFormMessage] = useState('')
  const [formMediaUrl, setFormMediaUrl] = useState('')
  const [formIdempotencyKey, setFormIdempotencyKey] = useState<string>(() =>
    generateIdempotencyKey(),
  )
  const [formError, setFormError] = useState<string | null>(null)
  const [sendFeedback, setSendFeedback] = useState<
    { color: 'green' | 'red'; message: string } | null
  >(null)

  const [debouncedSource] = useDebouncedValue(filterSource, 300)
  const [debouncedTarget] = useDebouncedValue(filterTarget, 300)

  // WebSocket: assina eventos para invalidar a tabela em tempo real.
  // Passa apenas o prefixo da queryKey para invalidar todas as paginacoes/filtros.
  const { wsStatus, lastEventAt } = useWhatsApiWs(
    tenantId ?? null,
    ['whatsapi-messages', tenantId],
  )

  // Chaves do select de envio: as do tenant, ou as ativas com gateway da organizacao.
  const { data: apiKeys = [] } = useQuery<Pick<WaApiKey, 'id' | 'name' | 'key_prefix' | 'is_active'>[], Error>({
    queryKey: modoOrg ? ['whatsapi-org-chaves', orgId] : ['whatsapi-api-keys', tenantId],
    queryFn:  () => modoOrg
      ? whatsapiOrgApi.chaves(orgId)
      : whatsapiApi.listApiKeys(tenantId as number),
    enabled:  (modoOrg || !!tenantId) && canSend,
    retry:    false,
  })

  const activeApiKeys = useMemo(() => apiKeys.filter((k) => k.is_active), [apiKeys])

  // Quando carrega/atualiza a lista, garante que o select tem uma key valida.
  useEffect(() => {
    if (!canSend) return
    if (formApiKeyId && activeApiKeys.some((k) => k.id === formApiKeyId)) return
    setFormApiKeyId(activeApiKeys[0]?.id ?? null)
  }, [activeApiKeys, formApiKeyId, canSend])

  // Auto-clear do feedback inline.
  useEffect(() => {
    if (!sendFeedback) return
    const t = setTimeout(() => setSendFeedback(null), FEEDBACK_AUTO_CLEAR_MS)
    return () => clearTimeout(t)
  }, [sendFeedback])

  // Reset para pagina 1 sempre que qualquer filtro (ou o escopo) mudar.
  useEffect(() => {
    setPage(1)
  }, [chaveEscopo, filterStatus, debouncedSource, debouncedTarget, filterDateFrom, filterDateTo])

  const { data, isLoading, error } = useQuery<WaMessageListResponse, Error>({
    queryKey: [
      'whatsapi-messages',
      chaveEscopo,
      page,
      filterStatus,
      debouncedSource,
      debouncedTarget,
      filterDateFrom,
      filterDateTo,
    ],
    queryFn: () => {
      const params = {
        page,
        page_size: PAGE_SIZE,
        status: filterStatus || undefined,
        source: debouncedSource || undefined,
        target: debouncedTarget || undefined,
        from:   filterDateFrom || undefined,
        to:     filterDateTo || undefined,
      }
      return modoOrg
        ? whatsapiOrgApi.listMessages(orgId, params)
        : whatsapiApi.listMessages(tenantId as number, params)
    },
    enabled: modoOrg || !!tenantId,
    refetchInterval: modoOrg ? POLLING_ORG_MS : false,
    retry: false,
  })

  function handle404NotFound(
    err: any,
    onAfter?: () => void,
  ): boolean {
    const is404 = err?.response?.status === 404
    const detail = err?.response?.data?.detail
    if (is404 && detail === 'message_not_found') {
      notifications.show({
        color: 'orange',
        title: 'Mensagem não encontrada',
        message: 'A mensagem pode ter sido removida ou não pertence a este tenant.',
      })
      queryClient.invalidateQueries({ queryKey: ['whatsapi-messages'] })
      onAfter?.()
      return true
    }
    return false
  }

  const resendMutation = useMutation<WaMessage, Error, string>({
    mutationFn: (id: string) => whatsapiApi.resendMessage(exigirTenant(tenantId), id),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Mensagem reenviada com sucesso.' })
      queryClient.invalidateQueries({ queryKey: ['whatsapi-messages'] })
      setSelectedMessage(null)
    },
    onError: (err: any) => {
      if (handle404NotFound(err, () => setSelectedMessage(null))) return
      notifications.show({ color: 'red', message: 'Falha ao reenviar a mensagem.' })
    },
  })

  const deleteMutation = useMutation<WaMessage, Error, string>({
    mutationFn: (id: string) => whatsapiApi.deleteMessage(exigirTenant(tenantId), id),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Mensagem deletada com sucesso.' })
      queryClient.invalidateQueries({ queryKey: ['whatsapi-messages'] })
      setDeleteTarget(null)
      setSelectedMessage(null)
    },
    onError: (err: any) => {
      if (handle404NotFound(err, () => {
        setDeleteTarget(null)
        setSelectedMessage(null)
      })) return
      notifications.show({ color: 'red', message: 'Falha ao deletar a mensagem.' })
    },
  })

  const sendMutation = useMutation<WaMessage, Error, WaSendPayload>({
    mutationFn: (payload) => modoOrg
      ? whatsapiOrgApi.sendMessage(exigirOrganizacao(orgId), payload)
      : whatsapiApi.sendMessage(exigirTenant(tenantId), payload),
    onSuccess: () => {
      setSendFeedback({ color: 'green', message: 'Mensagem enviada para a fila.' })
      setFormTarget('')
      setFormMessage('')
      setFormMediaUrl('')
      setFormIdempotencyKey(generateIdempotencyKey())
      queryClient.invalidateQueries({ queryKey: ['whatsapi-messages'] })
    },
    onError: (err) => {
      setSendFeedback({
        color: 'red',
        message: modoOrg ? resolveOrgSendError(err) : resolveSendError(err),
      })
      // `envio_falhou` grava a mensagem como falha: a lista mostra na hora.
      if (modoOrg) queryClient.invalidateQueries({ queryKey: ['whatsapi-messages'] })
    },
  })

  const handleSubmitSend = useCallback(() => {
    setFormError(null)
    if (!formApiKeyId) {
      setFormError('Selecione uma API key ativa.')
      return
    }
    const target = normalizeTarget(formTarget)
    if (!target || target === WA_TARGET_SUFFIX) {
      setFormError('Informe um target (número ou jid @s.whatsapp.net).')
      return
    }
    const message = formMessage.trim()
    const mediaUrl = formMediaUrl.trim()
    if (!message && !mediaUrl) {
      setFormError('Informe uma mensagem ou uma URL de media.')
      return
    }
    const payload: WaSendPayload = {
      api_key_id:      formApiKeyId,
      target,
      message,
      idempotency_key: formIdempotencyKey,
    }
    if (mediaUrl) payload.media_url = mediaUrl
    sendMutation.mutate(payload)
  }, [formApiKeyId, formTarget, formMessage, formMediaUrl, formIdempotencyKey, sendMutation])

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const clearFilters = () => {
    setFilterStatus('')
    setFilterSource('')
    setFilterTarget('')
    setFilterDateFrom('')
    setFilterDateTo('')
    setPage(1)
  }

  // Reenviar e apagar sao so do tenant (spec 2026-10-23 §3).
  const canResend = !modoOrg && can('whatsapi', 'resend')
  const canDelete = !modoOrg && can('whatsapi', 'delete')

  // Organização ativa com a lista a caminho: o escopo ainda é o tenant provisório. Esperar,
  // em vez de mostrar o título e os vazios do tenant e trocar em seguida.
  if (carregando) {
    return <Skeleton height={120} radius="sm" />
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Group gap="md" align="center">
          <TituloDoEscopo titulo="Mensagens" escopo={escopo} nome={nome} />
          <SeletorEscopo escopo={escopo} opcoes={opcoes} escolher={escolher} />
        </Group>
        {modoOrg ? (
          <Text size="xs" c="dimmed">Atualiza a cada 15 s</Text>
        ) : (
          <WhatsApiWsIndicator status={wsStatus} lastEventAt={lastEventAt} />
        )}
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar as mensagens. Tente novamente em instantes.
        </Alert>
      )}

      {canSend && (modoOrg || tenantId != null) && (
        <DataCard flush title="Enviar mensagem" accent="primary">
          <Stack gap="xs" p="md">
            {activeApiKeys.length === 0 ? (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="yellow"
                variant="light"
              >
                {modoOrg
                  ? 'Nenhuma chave ativa com gateway nesta organização. A chave se cria em WhatsApi › API Keys, na visão da organização.'
                  : 'Nenhuma API key ativa. Crie ou ative uma key em "API Keys".'}
              </Alert>
            ) : (
              <>
                <Group gap="xs" align="end" wrap="wrap">
                  <Select
                    label="API key"
                    size="sm"
                    aria-label="API key de envio"
                    value={formApiKeyId}
                    onChange={(v) => setFormApiKeyId(v)}
                    data={activeApiKeys.map((k) => ({
                      value: k.id,
                      label: `${k.name} (${k.key_prefix})`,
                    }))}
                    w={240}
                    allowDeselect={false}
                  />
                  <TextInput
                    label="Target"
                    size="sm"
                    placeholder="5511999990000 ou jid@s.whatsapp.net"
                    aria-label="Target do envio"
                    value={formTarget}
                    onChange={(e) => setFormTarget(e.currentTarget.value)}
                    w={300}
                  />
                  <TextInput
                    label="Media URL (opcional)"
                    size="sm"
                    placeholder="https://..."
                    aria-label="Media URL"
                    value={formMediaUrl}
                    onChange={(e) => setFormMediaUrl(e.currentTarget.value)}
                    w={300}
                  />
                </Group>
                <Textarea
                  label="Mensagem"
                  size="sm"
                  aria-label="Texto da mensagem"
                  placeholder="Texto a enviar (obrigatório se não houver mídia)"
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.currentTarget.value)}
                  minRows={2}
                  autosize
                />
                <Group justify="space-between" align="center" wrap="wrap">
                  <Text size="xs" c="dimmed" aria-label="Idempotency key">
                    idempotency_key: <Box component="span" style={{ fontFamily: 'monospace' }}>
                      {formIdempotencyKey}
                    </Box>
                  </Text>
                  <Button
                    size="sm"
                    leftSection={<IconSend size={14} />}
                    loading={sendMutation.isPending}
                    onClick={handleSubmitSend}
                  >
                    Enviar
                  </Button>
                </Group>
                {formError && (
                  <Alert
                    color="red"
                    variant="light"
                    icon={<IconAlertCircle size={16} />}
                    role="alert"
                  >
                    {formError}
                  </Alert>
                )}
                {sendFeedback && (
                  <Alert
                    color={sendFeedback.color}
                    variant="light"
                    role="status"
                    aria-label="Status do envio"
                  >
                    {sendFeedback.message}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </DataCard>
      )}

      <Group gap="xs" align="end" wrap="wrap">
        <Select
          label="Status"
          placeholder="Todos"
          clearable
          size="sm"
          aria-label="Filtrar por status"
          value={filterStatus || null}
          onChange={(v) => setFilterStatus((v as WaMessageStatus) ?? '')}
          data={STATUS_OPTIONS}
          w={160}
        />
        <TextInput
          label="Source"
          placeholder="ex: app-x"
          size="sm"
          aria-label="Filtrar por source"
          value={filterSource}
          onChange={(e) => setFilterSource(e.currentTarget.value)}
          w={160}
        />
        <TextInput
          label="Target"
          placeholder="ex: 5511..."
          size="sm"
          aria-label="Filtrar por target"
          value={filterTarget}
          onChange={(e) => setFilterTarget(e.currentTarget.value)}
          w={180}
        />
        <TextInput
          label="De"
          type="date"
          size="sm"
          aria-label="Data inicial"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.currentTarget.value)}
          w={160}
        />
        <TextInput
          label="Até"
          type="date"
          size="sm"
          aria-label="Data final"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.currentTarget.value)}
          w={160}
        />
        <Button
          variant="default"
          size="sm"
          leftSection={<IconFilterOff size={14} />}
          onClick={clearFilters}
        >
          Limpar filtros
        </Button>
      </Group>

      <DataCard flush
        title="Mensagens"
        accent="primary"
        badge={{ value: total, color: 'blue' }}
        loading={false}
      >
        {isLoading ? (
          <Stack gap="xs" p="md">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={20} radius="sm" />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            Nenhuma mensagem encontrada
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Target</Table.Th>
                <Table.Th>Mensagem</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Latência (ms)</Table.Th>
                <Table.Th>Criado em</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((m) => (
                <Table.Tr
                  key={m.id}
                  onClick={() => setSelectedMessage(m)}
                  style={{ cursor: 'pointer' }}
                  aria-label={`Ver detalhes da mensagem ${m.id}`}
                >
                  <Table.Td>
                    <Text size="sm">{m.target}</Text>
                  </Table.Td>
                  <Table.Td title={m.message}>
                    <Text size="sm">{truncate(m.message, 60)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{m.source ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <WhatsApiStatusBadge status={m.status} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {m.latency_ms !== null ? m.latency_ms : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{formatDateTime(m.created_at)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>

      {total > PAGE_SIZE && (
        <Group justify="center">
          <Pagination
            value={page}
            onChange={setPage}
            total={totalPages}
            size="sm"
            aria-label="Paginação das mensagens"
          />
        </Group>
      )}

      {/* Modal de detalhes */}
      <Modal
        opened={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        title="Detalhes da mensagem"
        size="lg"
      >
        {selectedMessage && (
          <Stack gap="xs">
            <DetailRow label="ID">{selectedMessage.id}</DetailRow>
            <DetailRow label="API key ID">{selectedMessage.api_key_id}</DetailRow>
            <DetailRow label="Target">{selectedMessage.target}</DetailRow>
            <DetailRow label="Mensagem">{selectedMessage.message}</DetailRow>
            <DetailRow label="Media URL">{selectedMessage.media_url ?? '—'}</DetailRow>
            <DetailRow label="Message ID">{selectedMessage.message_id ?? '—'}</DetailRow>
            <DetailRow label="Idempotency key">
              {selectedMessage.idempotency_key ?? '—'}
            </DetailRow>
            <DetailRow label="Status">
              <WhatsApiStatusBadge status={selectedMessage.status} />
            </DetailRow>
            <DetailRow label="Latência (ms)">
              {selectedMessage.latency_ms !== null ? selectedMessage.latency_ms : '—'}
            </DetailRow>
            <DetailRow label="Criado em">
              {formatDateTime(selectedMessage.created_at)}
            </DetailRow>
            <DetailRow label="Enviado em">
              {formatNullableDateTime(selectedMessage.sent_at)}
            </DetailRow>
            <DetailRow label="Entregue em">
              {formatNullableDateTime(selectedMessage.delivered_at)}
            </DetailRow>
            <DetailRow label="Lido em">
              {formatNullableDateTime(selectedMessage.read_at)}
            </DetailRow>
            <DetailRow label="Reenviado de">
              {selectedMessage.resent_from_id ?? '—'}
            </DetailRow>
            <DetailRow label="Deletado em">
              {formatNullableDateTime(selectedMessage.deleted_at)}
            </DetailRow>
            <DetailRow label="Source">{selectedMessage.source ?? '—'}</DetailRow>
            <DetailRow label="Erro">{selectedMessage.error ?? '—'}</DetailRow>

            <Divider my="sm" />

            <Group justify="flex-end" gap="xs">
              {canResend && selectedMessage.status === 'failed' && (
                <Button
                  variant="default"
                  leftSection={<IconRefresh size={14} />}
                  loading={resendMutation.isPending}
                  onClick={() => resendMutation.mutate(selectedMessage.id)}
                >
                  Reenviar
                </Button>
              )}
              {canDelete && (
                <Button
                  color="red"
                  variant="light"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => {
                    const id = selectedMessage.id
                    setSelectedMessage(null)
                    setDeleteTarget(id)
                  }}
                >
                  Deletar
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Modal de confirmacao de delete */}
      <Modal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Confirmar delete"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Tem certeza que deseja deletar esta mensagem? Esta ação não pode ser desfeita.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Cancelar
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget) deleteMutation.mutate(deleteTarget)
              }}
            >
              Confirmar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
